package com.example.mystybag.common.views

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.ModalBottomSheet
import androidx.compose.material3.rememberModalBottomSheetState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.platform.LocalSoftwareKeyboardController
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import com.example.mystybag.common.extensions.AsyncImage
import com.example.mystybag.common.extensions.scrollEdgeFade
import com.example.mystybag.common.resources.SR
import com.example.mystybag.common.ui.components.custom.CheckIcon
import com.example.mystybag.common.ui.components.custom.DefaultCircleShapedTextField
import com.example.mystybag.common.ui.components.native.Texts
import com.example.mystybag.core.defines.TextFieldType
import com.example.mystybag.core.expected.LogHelper
import com.example.mystybag.core.expected.rememberIsWebDesktopLayout
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.core.extensions.getCountryFlag
import com.example.mystybag.core.model.CountryItem
import com.example.mystybag.core.resources.SRC
import com.example.mystybag.core.ui.theme.LocalTheme.p1
import com.example.mystybag.core.ui.theme.LocalTheme.p4
import com.example.mystybag.core.ui.theme.LocalTheme.p8
import com.example.mystybag.core.ui.theme.LocalTheme.p12
import com.example.mystybag.core.ui.theme.LocalTheme.p16
import com.example.mystybag.core.ui.theme.LocalTheme.p20
import com.example.mystybag.core.ui.theme.LocalTheme.p40
import com.example.mystybag.core.views.AppTheme
import dev.icerock.moko.resources.compose.painterResource
import dev.icerock.moko.resources.compose.stringResource

/** Преобразува code point в String (поддръжка на supplementary plane за емоджи). */
private fun codePointToString(codePoint: Int): String {
    return when {
        codePoint in 0x10000..0x10FFFF -> {
            val high = ((codePoint - 0x10000) shr 10) + 0xD800
            val low = ((codePoint - 0x10000) and 0x3FF) + 0xDC00
            charArrayOf(high.toChar(), low.toChar()).concatToString()
        }
        else -> codePoint.toChar().toString()
    }
}

/** Малък PNG за уеб: Skiko не рендерира регионални флаг-емоджи; Coil вече тегли HTTPS на JS. */
private fun countryFlagRasterUrl(isoAlpha2: String): String? {
    if (isoAlpha2.length != 2) return null
    return "https://flagcdn.com/w80/${isoAlpha2.lowercase()}.png"
}

/** Преобразува ISO 3166-1 alpha-2 код (напр. "BG") във флаг емоджи (🇧🇬). */
private fun countryFlagEmoji(isoAlpha2: String): String {
    if (isoAlpha2.length != 2) return ""
    val a = isoAlpha2.uppercase()
    return a.map { codePointToString(0x1F1E6 + (it.code - 65)) }.joinToString("")
}

/**
 * Обща контрола за избор на държава/телефонен код.
 * Показва заглавие "Държава", поле за търсене и списък с държави (име + код).
 * Използва се навсякъде, където се избира код за държава.
 *
 * @param isVisible дали bottom sheet-ът е видим
 * @param countries списък с държави
 * @param selectedDialCode избраният телефонен код (за подсветка по код)
 * @param selectedCountryId опционално избрано ID на държава (за подсветка по ID, напр. при избор на адрес)
 * @param onCountrySelected callback при избор на държава
 * @param onDismiss callback при затваряне
 */
@Composable
fun CountryCodePickerBottomSheet(
    isVisible: Boolean,
    countries: List<CountryItem>,
    selectedDialCode: String? = null,
    selectedCountryId: Int? = null,
    onCountrySelected: (CountryItem) -> Unit,
    onDismiss: () -> Unit
) {
    if (rememberIsWebDesktopLayout()) {
        CountryCodePickerWebDialog(
            isVisible = isVisible,
            countries = countries,
            selectedDialCode = selectedDialCode,
            selectedCountryId = selectedCountryId,
            onCountrySelected = onCountrySelected,
            onDismiss = onDismiss
        )
        return
    }
    CountryCodePickerMobileBottomSheet(
        isVisible = isVisible,
        countries = countries,
        selectedDialCode = selectedDialCode,
        selectedCountryId = selectedCountryId,
        onCountrySelected = onCountrySelected,
        onDismiss = onDismiss
    )
}

@Composable
private fun CountryCodePickerWebDialog(
    isVisible: Boolean,
    countries: List<CountryItem>,
    selectedDialCode: String?,
    selectedCountryId: Int?,
    onCountrySelected: (CountryItem) -> Unit,
    onDismiss: () -> Unit
) {
    if (!isVisible) return

    val keyboardController = LocalSoftwareKeyboardController.current
    val theme = AppTheme.current.colors
    var searchQuery by remember { mutableStateOf("") }

    LaunchedEffect(isVisible) {
        if (isVisible) searchQuery = ""
    }

    Dialog(
        onDismissRequest = onDismiss,
        properties = DialogProperties(
            usePlatformDefaultWidth = false,
            dismissOnBackPress = true,
            dismissOnClickOutside = true
        )
    ) {
        Box(
            modifier = Modifier
                .fillMaxSize()
                .background(theme.surface),
            contentAlignment = Alignment.TopCenter
        ) {
            Column(
                modifier = Modifier
                    .width(530.dp)
                    .fillMaxHeight()
                    .padding(top = 32.dp, bottom = 32.dp),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(bottom = 24.dp)
                ) {
                    Texts.DisplaySmall(
                        text = stringResource(SR.strings.country),
                        modifier = Modifier.fillMaxWidth(),
                        textAlign = TextAlign.Center,
                        color = theme.onSurface
                    )
                    WebDialogCloseButton(
                        modifier = Modifier
                            .align(Alignment.CenterEnd)
                            .size(40.dp),
                        debugName = "CountryCodePickerWebDialog",
                        onClick = {
                            LogHelper.logEvent("[WebDialogCloseButton] CountryCodePickerWebDialog close")
                            keyboardController?.hide()
                            onDismiss()
                        }
                    )
                }

                DefaultCircleShapedTextField(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(bottom = 16.dp),
                    value = searchQuery,
                    onValueChange = { searchQuery = it },
                    label = sharedString(SR.strings.search_country),
                    inputType = TextFieldType.ANY,
                    keyboardOptions = KeyboardOptions(
                        keyboardType = KeyboardType.Text,
                        imeAction = ImeAction.Search
                    )
                )

                val filteredCountries = if (searchQuery.isBlank()) countries
                else countries.filter {
                    it.name.contains(searchQuery, ignoreCase = true) ||
                        it.dialCode.contains(searchQuery)
                }

                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .weight(1f, fill = false)
                        .verticalScroll(rememberScrollState())
                        .scrollEdgeFade()
                ) {
                    filteredCountries.forEachIndexed { index, country ->
                        if (index > 0) {
                            HorizontalDivider(
                                modifier = Modifier.fillMaxWidth(),
                                thickness = 1.dp,
                                color = theme.outlineVariant.copy(alpha = 0.5f)
                            )
                        }
                        CountryPickerRow(
                            country = country,
                            isSelected = (selectedCountryId != null && country.id == selectedCountryId) ||
                                (selectedDialCode != null && country.dialCode == selectedDialCode),
                            useWebFlagUrl = true,
                            rowVerticalPadding = p20,
                            onClick = {
                                keyboardController?.hide()
                                onCountrySelected(country)
                            }
                        )
                    }
                }

                Spacer(modifier = Modifier.height(16.dp))
            }
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun CountryCodePickerMobileBottomSheet(
    isVisible: Boolean,
    countries: List<CountryItem>,
    selectedDialCode: String?,
    selectedCountryId: Int?,
    onCountrySelected: (CountryItem) -> Unit,
    onDismiss: () -> Unit
) {
    val sheetState = rememberModalBottomSheetState(skipPartiallyExpanded = true)
    val colors = AppTheme.current.colors
    var searchQuery by remember { mutableStateOf("") }
    val countryTitle = stringResource(SR.strings.country)

    LaunchedEffect(isVisible) {
        if (isVisible) {
            sheetState.show()
        } else {
            sheetState.hide()
            searchQuery = ""
        }
    }

    if (!isVisible) return

    ModalBottomSheet(
        onDismissRequest = onDismiss,
        sheetState = sheetState,
        containerColor = colors.surface
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = p16)
        ) {
            ActionSheetHeader(title = countryTitle, onCloseClick = onDismiss)

            DefaultCircleShapedTextField(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(bottom = p16),
                value = searchQuery,
                onValueChange = { searchQuery = it },
                label = sharedString(SR.strings.search_country),
                inputType = TextFieldType.ANY,
                keyboardOptions = KeyboardOptions(
                    keyboardType = KeyboardType.Text,
                    imeAction = ImeAction.Search
                )
            )

            val filteredCountries = if (searchQuery.isBlank()) countries
            else countries.filter {
                it.name.contains(searchQuery, ignoreCase = true) ||
                    it.dialCode.contains(searchQuery)
            }

            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .verticalScroll(rememberScrollState())
                    .scrollEdgeFade()
            ) {
                filteredCountries.forEachIndexed { index, country ->
                    CountryPickerRow(
                        country = country,
                        isSelected = (selectedCountryId != null && country.id == selectedCountryId) ||
                            (selectedDialCode != null && country.dialCode == selectedDialCode),
                        useWebFlagUrl = false,
                        rowVerticalPadding = p8,
                        onClick = { onCountrySelected(country) }
                    )
                    if (index < filteredCountries.size - 1) {
                        HorizontalDivider(
                            modifier = Modifier.padding(vertical = p8),
                            color = colors.outline.copy(alpha = 0.12f)
                        )
                    }
                }
            }

            Spacer(modifier = Modifier.height(p16))
        }
    }
}

@Composable
private fun CountryPickerRow(
    country: CountryItem,
    isSelected: Boolean,
    useWebFlagUrl: Boolean,
    rowVerticalPadding: Dp,
    onClick: () -> Unit
) {
    val colors = AppTheme.current.colors
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick)
            .padding(vertical = rowVerticalPadding),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(p12)
    ) {
        Box(
            modifier = Modifier
                .size(p40 + p1 + p1)
                .aspectRatio(1f)
                .border(p1, colors.outline, CircleShape),
            contentAlignment = Alignment.Center
        ) {
            Box(
                modifier = Modifier
                    .size(p40)
                    .aspectRatio(1f)
                    .clip(CircleShape)
                    .background(colors.surfaceVariant.copy(alpha = 0.5f)),
                contentAlignment = Alignment.Center
            ) {
                if (useWebFlagUrl) {
                    val url = remember(country.isoAlpha2) {
                        countryFlagRasterUrl(country.isoAlpha2)
                    }
                    if (url != null) {
                        AsyncImage(
                            link = url,
                            modifier = Modifier.size(p40),
                            contentScale = ContentScale.Crop,
                            alignment = Alignment.Center,
                        )
                    }
                } else {
                    val flagRes = SRC.images.getCountryFlag(country.isoAlpha2)
                    if (flagRes != null) {
                        Image(
                            painter = painterResource(flagRes),
                            contentDescription = null,
                            modifier = Modifier.size(p40),
                            contentScale = ContentScale.Crop
                        )
                    } else {
                        Texts.BodyMedium(
                            text = countryFlagEmoji(country.isoAlpha2),
                            textAlign = TextAlign.Center,
                            modifier = Modifier.padding(p4)
                        )
                    }
                }
            }
        }
        Texts.BodyLarge(
            text = country.name,
            color = if (isSelected) colors.primary else colors.onSurface,
            modifier = Modifier.weight(1f)
        )
        if (isSelected) {
            CheckIcon(
                contentDescription = sharedString(SR.strings.selected),
                tint = colors.primary
            )
        }
    }
}
